import { LINKPAY_COOKIES } from "../../pages/constants";
import { Nullable } from "../types/types";
import { useCookie } from "./useCookie";

export interface ILinkPayCookies {
	hasDefaultCard: Nullable<string>;
	accessToken: Nullable<string>;
	uid: Nullable<string>;
	isAuthenticated: boolean;
	hasPaymentMethod: boolean;
	isFullySetup: boolean;
}

interface IUseLinkPayCookiesOptions {
	/**
	 * A passive reader takes one snapshot per mount: no reload-on-late-injection
	 * recovery, and no 1s polling. The payment surfaces keep both (their boot-time
	 * state genuinely needs a fresh start when the FairPrice app injects cookies
	 * late); anything mounted app-wide must read passively, or every venue inherits
	 * a whole-app reload and three 1Hz cookie parses for the life of the session.
	 */
	passive?: boolean;
}

export const useLinkPayCookies = (options?: IUseLinkPayCookiesOptions): ILinkPayCookies => {
	const refreshOnRetrySuccess = !options?.passive;
	const watch = !options?.passive;
	const { cookieValue: hasDefaultCard } = useCookie<string>(
		LINKPAY_COOKIES.HAS_DEFAULT_CARD,
		null,
		{ retryInterval: 5, refreshOnRetrySuccess, maxRetryDuration: 60, watch }
	);
	const { cookieValue: accessToken } = useCookie<string>(LINKPAY_COOKIES.ACCESS_TOKEN, null, {
		retryInterval: 5,
		refreshOnRetrySuccess,
		maxRetryDuration: 60,
		watch,
	});
	const { cookieValue: uid } = useCookie<string>(LINKPAY_COOKIES.UID, null, {
		retryInterval: 5,
		refreshOnRetrySuccess,
		maxRetryDuration: 60,
		watch,
	});

	const isAuthenticated = !!(accessToken && uid);
	const hasPaymentMethod = !!hasDefaultCard;
	const isFullySetup = !!(hasDefaultCard && accessToken && uid);

	return {
		hasDefaultCard,
		accessToken,
		uid,
		isAuthenticated,
		hasPaymentMethod,
		isFullySetup,
	};
};
